import { ModeType } from '@/types/graph'

/**
 * ENG-2588 — zoom behavior for the P&ID annotation tool.
 *
 * The pure decision behind the d3-zoom `.filter(...)` in PnidRenderer. It
 * separates the ZOOM gesture from the PAN gesture so zoom works the same in
 * every mode (it used to be torn off entirely in Select mode, killing
 * scroll-zoom during annotation):
 *
 *  - Wheel / trackpad pinch → ALWAYS zoom, cursor-anchored, in every mode.
 *    Browsers deliver a trackpad pinch as a ctrl+wheel event, so we must let
 *    ctrl+wheel through — exactly what d3's default filter does via its
 *    `event.type === 'wheel'` clause (which the previous custom filter dropped).
 *  - 2+ finger touch → ALWAYS pinch-zoom, in every mode.
 *  - Drag (mouse / single finger) → PAN only with the hand tool (Drag mode),
 *    the Spacebar held, or the middle mouse button. In Select/Create the
 *    primary drag is the rubber-band / new-shape draw, so d3 must NOT also pan.
 *
 * Keeping this DOM-free is what lets it be unit-tested; the PnidRenderer
 * wrapper layers the connection-marker guard on top.
 */

/**
 * Minimal, framework-agnostic view of an input event — only the fields the
 * gesture filter reads.
 */
export interface ZoomGestureEvent {
  /** DOM event type: 'wheel' | 'mousedown' | 'touchstart' | 'dblclick' | … */
  type: string
  /** Mouse button (0 = primary/left, 1 = middle, 2 = right). 0 for non-mouse events. */
  button: number
  /** Whether Ctrl is held. Trackpad pinch arrives as a ctrl+wheel event. */
  ctrlKey: boolean
  /** Active touch points (TouchEvent.touches.length); 0 for non-touch events. */
  touchCount: number
}

export interface ZoomGestureState {
  /** The current editor tool/mode. */
  mode: ModeType
  /** True while the Spacebar is held — temporary pan in any mode (Figma/tldraw style). */
  spacePanning: boolean
}

/** Whether d3-zoom should handle this event (vs. letting the app draw/select). */
export function shouldD3HandleGesture(
  event: ZoomGestureEvent,
  state: ZoomGestureState,
): boolean {
  // Wheel always zooms — cursor-anchored — including ctrl+wheel (trackpad pinch).
  if (event.type === 'wheel') return true

  // Multi-touch pinch zooms in any mode.
  if (event.touchCount >= 2) return true

  // Everything below is a drag = pan. A ctrl-modified press is a secondary /
  // context action (e.g. macOS ctrl-click), never a pan — matches d3's default.
  if (event.ctrlKey) return false

  // Middle mouse button pans in any mode.
  if (event.button === 1) return true

  // Any other non-primary button (right / back / forward) never pans.
  if (event.button !== 0) return false

  // Primary drag pans only with the hand tool or while the Spacebar is held;
  // otherwise it is a draw / select gesture the app handles itself.
  return state.mode === ModeType.Drag || state.spacePanning
}

/**
 * Whether a canvas pointer-down is a PAN gesture rather than a draw/select.
 * A pan begins with the hand tool (Drag mode), the Spacebar held, or the middle
 * mouse button — in all three the press must NOT start a rubber-band/draw and
 * must NOT be treated as a deselecting canvas click. (ENG-2588: panning with a
 * node selected was firing the empty-canvas click that closed the details panel.)
 */
export function isCanvasPanPress(opts: {
  mode: ModeType
  button: number
  spacePanning: boolean
}): boolean {
  return opts.spacePanning || opts.button === 1 || opts.mode === ModeType.Drag
}

/** Minimal view of the focused element for the Spacebar pan focus-guard. */
export interface FocusTarget {
  /** activeElement.tagName (the DOM upper-cases it, e.g. 'INPUT', 'SELECT'). */
  tagName: string
  /** activeElement.isContentEditable. */
  isContentEditable: boolean
  /** The element's `role` attribute, if any. */
  role: string | null
}

/**
 * Whether pressing Space should ARM the canvas pan, given what currently holds
 * focus and the editor mode. (ENG-2588 added Space-pan; ENG-2606 lets it fire
 * straight from the Add-Object panel.)
 *
 *  - A text field (INPUT / TEXTAREA / contentEditable) ALWAYS keeps Space — the
 *    user is typing a space into the Tag / Note input, so never pan from there.
 *  - A focused button / select / tab normally consumes Space (click / open), so
 *    OUTSIDE the draw flow it keeps it. But in Create mode the annotator wants to
 *    pan before drawing, so Space must win over the panel's controls — otherwise
 *    it re-opens the Shape dropdown or clicks the panel's close button.
 *  - Otherwise (canvas / body has focus) Space arms pan.
 */
export function shouldSpaceArmPan(target: FocusTarget | null, mode: ModeType): boolean {
  if (!target) return true
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return false
  const isClickableControl =
    tag === 'SELECT' ||
    tag === 'BUTTON' ||
    tag === 'A' ||
    target.role === 'button' ||
    target.role === 'menuitem' ||
    target.role === 'checkbox' ||
    target.role === 'tab'
  if (isClickableControl && mode !== ModeType.Create) return false
  return true
}
